<template>
	<core-page class="omapi-campaign-settings" v-if="routeId">
		<router-link :to="{ name: 'campaigns' }" class="omapi-link-arrow-before omapi-button__link">Return to Campaigns List</router-link>
		<core-loading v-if="isLoading"><h1>Loading...</h1></core-loading>
		<form v-else @submit.prevent="save">
			<h1>
				<span>{{ title }}</span>
			</h1>
			<common-alerts id="om-notification-campaign" :alerts="alerts"/>

			<div class="omapi-card__flex omapi-campaign-settings-sections" :class="{'disabled' : isDisabled}">
				<common-card size="two-thirds"
					title="WordPress Output Settings"
					contentClass="omapi-card__border"
					footerClass="omapi-card__child"
					class="omapi-campaign-settings__wrapper"
				>
					<template v-slot:title>WordPress Output Settings</template>
					<p v-if="hasError" class="text-setting">An error was encountered.</p>
					<template v-else>
						<p class="text-setting">Control when and where your campaigns are displayed once the targeting conditions are met.</p>

						<campaigns-inline-settings
							:campaign="campaign"
							v-if="'Inline' === campaignType"
						/>

						<campaigns-mailpoet-settings
							:campaign="campaign"
							v-if="hasMailPoet"
						/>
					</template>

					<template v-slot:footer>
						<campaigns-advanced-settings v-if="! hasError" :campaign="campaign">
							<core-save-button
								color="green"
								:disabled="!changed"
								:saving="saving"
							/>
						</campaigns-advanced-settings>
					</template>
				</common-card>

				<campaigns-right-column
					:campaignId="id"
					:changed="changed"
					:saving="saving"
					:newStatus="newStatus"
					:statusText="statusText"
					@updateStatus="onChangeStatus"
				/>
			</div>
		</form>
	</core-page>
</template>

<script lang="js">
import get from 'lodash/get'
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { campaign } from '@/campaigns/mixins/campaign'
import { findDiffPosition } from '@/utils'

export default  {
	mixins : [ campaign ],
	data () {
		return {
			changed   : false,
			orig      : false,
			origMeta  : {},
			newStatus : '',
			saving    : false,
		}
	},
	mounted () {
		if (!this.routeId) {
			return
		}

		this.fetch()
		this.updateTitle()

		window.addEventListener('beforeunload', this.maybePreventExit)
	},
	beforeDestroy () {
		window.removeEventListener('beforeunload', this.maybePreventExit)
	},
	watch : {
		isLoading (isLoading) {
			if (!isLoading) {
				this.fetch()
			}
		},
		title () {
			this.updateTitle()
		},
		campaign (newVal, oldVal) {
			if (!this.fetchedCampaign || !get(oldVal, 'wp')) {
				return
			}

			if (!this.orig) {
				this.orig = this.getMetaString(oldVal)
			}

			if (this.newStatus !== this.campaignStatus) {
				this.changed = true
				return
			}
			newVal = this.getMetaString(newVal)
			const diff = findDiffPosition(newVal, this.orig)
			this.changed = -1 !== diff
		},
		newStatus (newStatus, oldStatus) {
			if (!this.changed) {
				this.changed = this.newStatus !== this.campaignStatus
			}
		},
	},
	beforeRouteLeave (to, from, next) {
		if (!this.changed || confirm('You have unsaved changes. Are you sure you want to lose these changes?')) {
			this.resetCampaign()
			return next()
		}
	},
	computed : {
		...mapState([ 'alerts' ]),
		...mapState('campaigns', [ 'campaigns' ]),
		...mapGetters('campaigns', [ 'getCampaign', 'hasError' ]),
		...mapGetters('wp', [ 'hasMailPoet' ]),
		...mapGetters([ 'isFetched', 'isFetching' ]),
		routeId () {
			return this.$get('$route.params.campaignId', '')
		},
		/**
		 * Get the campaign object.
		 * @returns {Object} The campaign object.
		 */
		campaign () {
			return this.getCampaign(this.routeId)
		},
		fetchedCampaign () {
			return this.isFetched(this.routeId)
		},
		fetchingCampaign () {
			return this.isFetching(this.routeId)
		},
		shouldFetch () {
			return !this.fetchingCampaign && !this.fetchedCampaign
		},
		isLoading () {
			if (this.showAlerts) {
				return false
			}
			return !this.fetchedCampaign ||
				this.$store.getters.isLoading([ 'campaigns', 'me' ])
		},
		isDisabled () {
			if (this.hasError) {
				return false
			}
			return this.isLoading || !this.isFetched('wpResources')
		},
		showAlerts () {
			return Boolean(this.alerts.length)
		},
		title () {
			return this.$get('campaign.name', 'No Campaign Found')
		},
		/**
		 * Get the appropriate text for the campaign status column.
		 *
		 * - Seeing "Draft" status always indicates app status is Paused
		 *   (regardless of embed code status)
		 *
		 * - Seeing "Pending" status always indicates either embed code status
		 *   is disabled, or app status is Paused
		 *
		 * - Seeing "Published" status always indicates app status is Active
		 *   and embed code status is enabled
		 *
		 * @returns {string} The text for the campaign status.
		 */
		statusText () {
			const published = 'active' !== this.newStatus

			if (this.isSplit) {
				return published ? 'Published' : 'Draft'
			}

			if (published) {
				return this.settingEnabled ? 'Pending' : 'Draft'
			}

			return this.settingEnabled ? 'Published' : 'Pending'
		},
	},
	methods : {
		...mapActions('campaigns', [ 'fetchCampaign', 'fetchWpDataForCampaign', 'saveWordPress' ]),
		...mapMutations([ 'fetching', 'fetched' ]),
		fetch () {
			if (this.fetchingCampaign) {
				// Already in progress.
				return
			}

			if (!this.fetchedCampaign) {
				this.fetching(this.routeId)
			}

			this.fetchCampaign({ campaignId: this.routeId })
				.then(() => this.fetchWpData())
				.catch(e => {})
		},
		fetchWpData () {
			const result = this.fetchedCampaign
				? Promise.resolve()
				: this.fetchWpDataForCampaign({ campaignId: this.routeId })

			return result
				.then(() => this.fetched(this.routeId))
				.then(() => this.afterFetched())
				.catch(e => {})
		},
		save () {
			this.$store.commit('clearAlerts')

			this.saving = true

			this.setStatus({ campaigns: [ this.id ], status: this.newStatus })
			this.saveWordPress({
				campaignId : this.id,
				settings   : this.getSettings(),
			}).then(() => {
				this.fetchWpData()
			})
		},
		afterFetched () {
			this.orig      = this.getMetaString(this.campaign)
			this.origMeta  = this.$get('campaign.wp.post_meta', {})
			this.newStatus = this.campaignStatus
			this.changed   = false
			this.saving    = false
		},
		updateTitle () {
			const title = this.$get('campaign.name', this.id)
			if (title) {
				this.$store.dispatch('setTitle', { title: `Output Settings: ${title}`, setBannerTitle: false })
				this.$store.commit('setBannerTitle', 'Campaign Output Settings')
			}
		},
		getMetaString (val) {
			const settings = this.getSettings(get(val, 'wp.post_meta', {}))
			return JSON.stringify(settings) + this.newStatus
		},
		/**
		 * The browser `beforeunload` callback to help catch users who are
		 * navigating away from our page, but have unsaved changes.
		 *
		 * See https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
		 *
		 * @param {Object} event The `beforeunload` event object.
		 *
		 * @returns {string} The message to display in the browser alert.
		 */
		maybePreventExit (event) {
			if (this.changed) {
				// Cancel the event
				event.preventDefault()

				// Chrome requires returnValue to be set
				event.returnValue = 'You have unsaved changes. Are you sure you want to lose these changes?'

				return event.returnValue
			}
		},
		resetCampaign () {
			this.newStatus = this.campaignStatus
			this.updateCampaignMeta({
				campaignId : this.id,
				meta       : this.origMeta,
			})
		},
		onChangeStatus (status) {
			switch (status) {
			case 'Pending':
				this.toggleEmbed('active' !== this.newStatus)
				break
			case 'Published':
				this.newStatus = 'active'
				this.toggleEmbed(true)
				break
			case 'Draft':
				this.newStatus = 'paused'
				this.toggleEmbed(false)
				break
			default:
				break
			}
		},
		/**
		 * Toggle the embed code on/off.
		 *
		 * @since  2.0.0
		 *
		 * @param  {boolean} enabled Whether to set embed enabled/disabled.
		 *
		 * @returns {void}
		 */
		toggleEmbed (enabled) {
			if (enabled !== this.settingEnabled) {
				this.settingEnabled = enabled
			}
		},
	},
}
</script>

<style lang="scss">
	@import '../assets/scss/variables';
	@import '../assets/scss/pages/campaign';
</style>
